import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { tokenStorage } from '../utils/tokenStorage';
import { authApi } from '../api/auth';

/**
 * SSO Callback Page
 * Handles the callback from TMT IT Group Employee Portal SSO login.
 * Stores the token and redirects to the dashboard.
 */
export default function SSOCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleSSOCallback = async () => {
      const token = searchParams.get('token');
      const refreshToken = searchParams.get('refreshToken');
      const redirect = searchParams.get('redirect') || '/dashboard';
      const errorParam = searchParams.get('error');

      if (errorParam) {
        setError(decodeURIComponent(errorParam));
        return;
      }

      if (token) {
        try {
          // Store the tokens
          tokenStorage.setAccessToken(token);
          if (refreshToken) {
            tokenStorage.setRefreshToken(refreshToken);
          }
          
          // Fetch and store user data to complete authentication
          const user = await authApi.getCurrentUser();
          tokenStorage.setUser(user);
          
          // Redirect to the intended destination
          // Use window.location for a full page refresh to reinitialize AuthContext
          window.location.href = redirect;
        } catch (err) {
          console.error('SSO callback error:', err);
          tokenStorage.clearAll();
          setError('Failed to complete login. Please try again.');
        }
      } else {
        setError('No authentication token received. Please try logging in again.');
      }
    };

    handleSSOCallback();
  }, [searchParams, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Login Failed</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Completing Login...</h2>
        <p className="text-gray-600">Please wait while we redirect you to the dashboard.</p>
      </div>
    </div>
  );
}
